import { useEffect, useRef } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import { AccountLogIn } from '@klv/atoms/account-log-in';
import { ScrollDepthIndicator } from '@klv/atoms/scroll-depth-indicator';
import SkipButton from '@klv/atoms/skip-button';
import { useScrollDirection } from '@klv/hooks/useScrollDirection';
import { AlertBar } from '@klv/molecules/alert-bar';
import { UniversalNavigation } from '@klv/organisms/universal-navigation';
import { useAuthentication } from '@klv/context/authentication';
import KlaviyoStatusIndicator from '@klv/organisms/klaviyo-status-indicator';
import { UtilityNavigation } from '@klv/organisms/utility-navigation';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/header.module.css';

import type { IHeader, IPlatformAction, ISiteAction } from './types';

export const Header = ({
  academyLoggedIn,
  alertBar,
  className = '',
  desktopLogoIconName,
  homeLink,
  invertOnHover = true,
  menuStyle = 'default',
  navigationBackgroundColor,
  navigationStyle = 'default',
  path,
  scrollIndicator = false,
  searchLink,
  siteMetadata,
  statusIndicator = false,
  universalCtas,
  universalNavigationItems,
  utilityChildren,
  utilityNavigationItems,
  ...props
}: IHeader) => {

  const headerRef = useRef<HTMLElement>(null);
  const isDefaultNav = navigationStyle !== 'minimal';
  
  const { t } = useTranslation();
  const scrollDirection = useScrollDirection();
  const { excludedPaths: alertBarExcludedPaths = [] } = alertBar || {};
  const pathIsExcludedAlertBarPath = alertBarExcludedPaths.includes(path) || alertBarExcludedPaths.some((excludedPath) => path.startsWith(excludedPath.replace('*', '')));
  const { authenticationState: { loggedIn } } = useAuthentication();

  useEffect(() => {
    if (headerRef?.current) {
      const layoutNavigationBottomEdge = getComputedStyle(headerRef.current).getPropertyValue('--layout-navigation-bottom-edge');
      if (layoutNavigationBottomEdge) {
        document.documentElement.style.setProperty('--layout-navigation-bottom-edge', layoutNavigationBottomEdge);
      }
    }
  }, [headerRef, scrollDirection]);

  const platformActions: IPlatformAction[] = [
    {
      component: <AccountLogIn />,
      label: 'AccountLogIn',
    },
  ];

  if (loggedIn) {
    platformActions.push({
      label: `${t('navigation_platformActions_support_label')}`,
      href: `${t('navigation_platformActions_support_href')}`,
    });
  }

  const siteActions: ISiteAction[] = [
    {
      label: `${t('navigation_siteActions_search_label')}`,
      href: searchLink ?? `${t('navigation_siteActions_search_href')}`,
      icon: 'Search',
    },
  ];


  return (
    <header
      id="primary-navigation-header"
      className={generateClassName([
        className,
        styles.header,
        scrollDirection === 'down' && styles.scrolled,
        navigationStyle === 'minimal' && styles.minimal,
      ])}
      ref={headerRef}
      {...props}
    >
      <SkipButton />
      {isDefaultNav && (
        <UtilityNavigation
          menuItems={utilityNavigationItems}
          path={path}
          platformActions={platformActions}
          scrollDirection={scrollDirection}
          siteActions={siteActions}
          siteMetadata={siteMetadata}
        >
          {statusIndicator ? <KlaviyoStatusIndicator /> : null}
          {utilityChildren}
        </UtilityNavigation>
      )}
      <UniversalNavigation
        academyLoggedIn={academyLoggedIn}
        color={navigationBackgroundColor}
        ctas={universalCtas}
        desktopLogoIconName={desktopLogoIconName}
        homeLink={homeLink}
        invertOnHover={invertOnHover}
        menuItems={universalNavigationItems}
        menuStyle={menuStyle}
        navigationStyle={navigationStyle}
        path={path}
        platformActions={platformActions}
        scrollDirection={scrollDirection}
        siteActions={siteActions}
        utilityItems={utilityNavigationItems}
      >
        {scrollIndicator ? <ScrollDepthIndicator /> : null}
      </UniversalNavigation>
      {(isDefaultNav && alertBar?.enabled && !pathIsExcludedAlertBarPath) ? (
        <AlertBar
          backgroundColor={alertBar?.backgroundColor}
          backgroundImage={alertBar?.backgroundImage}
          href={alertBar?.href}
          icon={alertBar?.showIcon ? alertBar?.icon : undefined}
          message={alertBar?.message}
          openInNewTab={alertBar?.openInNewTab}
          scrollDirection={scrollDirection}
          textColor={alertBar?.textColor}
        />
      ) : null}
    </header>
  );
};
